
import React, { useEffect, useMemo, useState,useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import HeaderMenu from "../../Header/HeaderMenu";

import OccupancyHeader from "../components/OccupancyHeader";
import OccupancySummary from "../components/OccupancySummary";
import OccupancyToolbar from "../components/OccupancyToolbar";
import OccupancyTimeline from "../components/OccupancyTimeline";
import BookingContextMenu from "../components/BookingContextMenu";
import RoomContextMenu from "../components/RoomContextMenu";
import RoomActionDialog from "../components/RoomActionDialog";
import StayDetailsDrawer from "../components/StayDetailsDrawer";
import NewStayDialog from "../components/NewStayDialog";

import useOccupancy from "../hooks/useOccupancy";

import axiosApi from "../../Utils/axiosApi";
import "../occupancy.css";

const nextDay = (v) => {
  const d = new Date(`${v}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function OccupancyPage() {
  const [hostelList, setHostelList] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState("");

  const [view, setView] = useState("fortnight");

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return d.toISOString().slice(0, 10);
  });

  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  const [bookingMenu, setBookingMenu] = useState(null);
  const [roomMenu, setRoomMenu] = useState(null);

  // Room dialog state
  const [roomDialog, setRoomDialog] = useState({
    open: false,
    action: null,
    room: null,
  });

  // Room details API state
  const [roomDetails, setRoomDetails] = useState(null);
  const [roomDetailsLoading, setRoomDetailsLoading] = useState(false);
  const [roomDetailsError, setRoomDetailsError] = useState("");

  // New Stay and existing Stay dialog state
  const [newStayOpen, setNewStayOpen] = useState(false);
  const [selectedStayId, setSelectedStayId] = useState(null);
  const [occupancyEditType, setOccupancyEditType] = useState(null);

  const [drawer, setDrawer] = useState({
    open: false,
    stay: null,
    mode: "view",
  });

  // Standardized notification state
  const [notice, setNotice] = useState({
    message: "",
    type: "info",
  });
// Custom confirmation dialog
const [confirmation, setConfirmation] = useState({
  open: false,
  title: "Confirm Action",
  message: "",
  confirmText: "Confirm",
  color: "primary",
});

const confirmationResolver = useRef(null);

const confirmAction = (message, options = {}) => {
  return new Promise((resolve) => {
    confirmationResolver.current = resolve;

    setConfirmation({
      open: true,
      title: options.title || "Confirm Action",
      message,
      confirmText: options.confirmText || "Confirm",
      color: options.color || "primary",
    });
  });
};

const closeConfirmation = () => {
  setConfirmation((previous) => ({
    ...previous,
    open: false,
  }));

  if (confirmationResolver.current) {
    confirmationResolver.current(false);
    confirmationResolver.current = null;
  }
};

const handleConfirmAction = () => {
  setConfirmation((previous) => ({
    ...previous,
    open: false,
  }));

  if (confirmationResolver.current) {
    confirmationResolver.current(true);
    confirmationResolver.current = null;
  }
};
  const showNotice = (message, type = "info") => {
    setNotice({
      message: String(message ?? ""),
      type,
    });
  };

  const clearNotice = () => {
    setNotice({
      message: "",
      type: "info",
    });
  };

  // Load hostels
  useEffect(() => {
    axiosApi
      .post("/hostel/getHostelName", {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.hostels || [];

        const list = data.map((item) => {
          if (Array.isArray(item)) {
            return {
              id: item[0],
              hostelName: item[1],
            };
          }

          return {
            id: item.id ?? item.hostelId,
            hostelName: item.hostelName ?? item.name,
          };
        });

        setHostelList(list);

        if (list.length > 0) {
          setSelectedHostel(String(list[0].id));
        }
      })
      .catch((err) => {
        console.error("Error fetching hostels:", err);
        showNotice("Unable to load the hostel list.", "error");
      });
  }, []);

  const {
    rooms,
    stays,
    loading,
    error,
    summary,
    updateStay,
    createStay,
    updateRoom,
  } = useOccupancy(selectedHostel || null, startDate, endDate);

  // Filter rooms by room number, type, or tenant name
  const filteredRooms = useMemo(() => {
    const q = search.toLowerCase().trim();

    if (!q) return rooms;

    const ids = new Set(
      stays
        .filter((s) =>
          (s.tenantName || "").toLowerCase().includes(q)
        )
        .map((s) => s.roomId)
    );

    return rooms.filter(
      (r) =>
        String(r.number || "").toLowerCase().includes(q) ||
        String(r.type || "").toLowerCase().includes(q) ||
        ids.has(r.id)
    );
  }, [rooms, stays, search]);

  

  const closeDrawer = () => {
    setDrawer((v) => ({ ...v, open: false }));
  };

  // Fetch details when a room is clicked on the timeline
  const openRoomDetails = (room) => {
    handleRoomAction("details", room);
  };

  // Open the existing SingleBill/NewStay dialog for a stay
  const openSingleBill = (stay) => {
    const id = stay?.id ?? stay?.stayId;

    if (id == null || id === "") {
      showNotice(
        "Unable to open stay: stay ID is missing.",
        "warning"
      );
      return;
    }

    setSelectedStayId(id);
    setNewStayOpen(true);
  };
const openOccupancyStay = (stay, editType) => {
  const id = stay?.id ?? stay?.stayId;

  if (id == null || id === "") {
    showNotice("Stay ID is missing.", "warning");
    return;
  }

  setOccupancyEditType(editType);
  setSelectedStayId(id);
  setNewStayOpen(true);
  setBookingMenu(null);
};
  const closeSingleBill = () => {
  setNewStayOpen(false);
  setSelectedStayId(null);
  setOccupancyEditType(null);
};

  

  // Save stay
  const saveStay = async (v, mode) => {
    if (mode === "edit") {
      setDrawer({ open: true, stay: v, mode: "edit" });
      return;
    }

    if (
  !(await confirmAction("Save these stay details?", {
    title: "Confirm Save",
    confirmText: "Yes, Save",
  }))
) {
  return;
}

    try {
      if (drawer.mode === "create" || !v.id) {
        await createStay(v);
      } else {
        await updateStay(v);
      }

      closeDrawer();
      showNotice("Stay saved successfully.", "success");
    } catch (e) {
      showNotice(
        e.message || "Failed to save stay.",
        "error"
      );
    }
  };

  // Booking context-menu actions


  // Open room actions and load API data for View Room Details
  const handleRoomAction = async (
    action,
    room,
    responseData = null
  ) => {
    setRoomMenu(null);

    setRoomDialog({
      open: true,
      action,
      room,
    });

    // Only the details action needs this API
    if (action !== "details") return;

    setRoomDetails(null);
    setRoomDetailsError("");

    const getRoomList = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.rooms)) return data.rooms;
      if (Array.isArray(data?.data?.rooms)) return data.data.rooms;

      return data && typeof data === "object" ? [data] : [];
    };

    // Use response data if already supplied by the context menu
    if (responseData !== null && responseData !== undefined) {
      const roomList = getRoomList(responseData);

      if (roomList.length === 0) {
        setRoomDetailsError(
          "No room details were returned by the API."
        );
        return;
      }

      setRoomDetails(roomList);
      return;
    }

    if (!selectedHostel) {
      setRoomDetailsError("Please select a hostel.");
      return;
    }

    setRoomDetailsLoading(true);

    try {
      const requestBody = {
        hostelSearchCriteria: {
          hostelIds: [selectedHostel],
          roomType: room?.roomType ?? room?.type ?? "",
        },
      };

      const response = await axiosApi.post(
        "/public/getHostelRoom",
        requestBody
      );

      console.log("Room details API response:", response.data);

      const roomList = getRoomList(response.data);

      if (roomList.length === 0) {
        setRoomDetailsError(
          "No room details were returned by the API."
        );
        return;
      }

      // Keep all inventory records; IDs may be null
      setRoomDetails(roomList);
    } catch (e) {
      console.error("Failed to load room details:", e);

      setRoomDetailsError(
        e.response?.data?.message ||
          "Failed to load room details."
      );
    } finally {
      setRoomDetailsLoading(false);
    }
  };

  const closeRoomDialog = () => {
    setRoomDialog({
      open: false,
      action: null,
      room: null,
    });

    setRoomDetails(null);
    setRoomDetailsError("");
    setRoomDetailsLoading(false);
  };

  // Save selected room action
 const saveRoomAction = async (data) => {
  const { action, room } = data;

  try {
    if (action === "maintenance") {
      const confirmed = await confirmAction(
        `Are you sure you want to mark room ${room.number} for maintenance?`,
        {
          title: "Confirm Maintenance",
          confirmText: "Yes, Continue",
        }
      );

      if (!confirmed) return;

      await updateRoom({
        ...room,
        status: "MAINTENANCE",
      });

      showNotice(
        `Room ${room.number} marked for maintenance.`,
        "success"
      );

      closeRoomDialog();
      return;
    }

    if (action === "cleaning") {
      showNotice(
        `Cleaning task details prepared for room ${room.number}. Connect the cleaning API to save the task.`,
        "info"
      );

      closeRoomDialog();
      return;
    }

    // Details and schedule are view-only actions
    closeRoomDialog();
  } catch (e) {
    showNotice(
      e.message || "Failed to process room action.",
      "error"
    );
  }
};


 // Move a stay to another valid room using drag-and-drop
const dropStay = async (id, targetRoomId) => {
  // Find the stay being moved
  const s = stays.find(
    (x) => String(x.id ?? x.stayId) === String(id)
  );

  // Find target room using either normalized ID or API roomRowId
  const r = rooms.find(
    (x) =>
      String(x.id ?? x.roomRowId) === String(targetRoomId)
  );

  if (!s || !r) {
    showNotice("Stay or target room was not found.", "error");
    return;
  }

  // Support both normalized and API response field names
  const actualRoomId = r.roomRowId ?? r.id;

  const targetRoomNumber = String(
    r.number ?? r.roomNumber ?? ""
  ).trim();

  const targetRoomType =
    r.type ?? r.roomType ?? r.roomTypeName ?? "";

  // Reject dummy and unassigned rooms
  const isInvalidRoom =
    String(r.status ?? "").toUpperCase() === "UNASSIGNED" ||
    !targetRoomNumber ||
    targetRoomNumber.toLowerCase() === "unassigned" ||
    targetRoomNumber.toUpperCase().startsWith("ROOM-");

  if (isInvalidRoom) {
    showNotice(
      "Bookings cannot be moved to dummy or unassigned rooms.",
      "warning"
    );
    return;
  }

  // Do not move to the same room
  if (String(s.roomId) === String(actualRoomId)) {
    return;
  }

  const confirmed = await confirmAction(
  `Are you sure you want to move ${s.tenantName} to room ${targetRoomNumber}?`,
  {
    title: "Confirm Room Transfer",
    confirmText: "Yes, Move",
  }
);

if (!confirmed) return;

  try {
    await updateStay({
      ...s,
      operation: "MOVE",

      // Actual destination room identity
      targetRoomId: actualRoomId,

      // Destination room information
      targetRoomNumber,
      targetRoomType,
    });

    showNotice(
      `Moved ${s.tenantName} to room ${targetRoomNumber}.`,
      "success"
    );
  } catch (e) {
    console.error("Room transfer failed:", e);

    showNotice(
      e.message || "Room transfer failed.",
      "error"
    );
  }
};

  // Resize a stay by changing check-in or check-out
  const resizeStay = async (s, edge, delta) => {
    const key = edge === "start" ? "checkIn" : "checkOut";
    const d = new Date(`${s[key]}T12:00:00`);

    d.setDate(d.getDate() + delta);

    const v = {
      ...s,
      [key]: d.toISOString().slice(0, 10),
    };

    if (v.checkOut <= v.checkIn) return;

    try {
      await updateStay(v);
    } catch (e) {
      showNotice(
        e.message || "Date change is invalid.",
        "error"
      );
    }
  };
const bookingAction = async (action, stay) => {
  const s = stays.find(
    (x) =>
      String(x.id ?? x.stayId) ===
      String(stay.id ?? stay.stayId)
  );

  if (!s) {
    showNotice("Booking details not found.", "error");
    return;
  }

  try {
    // 1. Tenant Details – open full editing mode
    if (action === "tenant") {
      openOccupancyStay(s, "all");
      return;
    }

    // 2. Update Stay Details – open date-only editing mode
    if (action === "dates") {
      openOccupancyStay(s, "dates");
      return;
    }

    // 3. Transfer Room – use existing drag-and-drop functionality
    if (action === "move") {
      showNotice(
        "Drag this booking to another room to transfer it.",
        "info"
      );
      setBookingMenu(null);
      return;
    }

    // 4. Cancel Stay
    if (action === "cancel") {
      const confirmed = await confirmAction(
        `Are you sure you want to cancel the stay for ${s.tenantName}?`,
        {
          title: "Confirm Stay Cancellation",
          confirmText: "Yes, Cancel Stay",
          color: "error",
        }
      );

      if (!confirmed) return;

      await updateStay({
        ...s,
        status: "CANCELLED",
      });

      showNotice(
        "Stay cancelled successfully.",
        "success"
      );

      setBookingMenu(null);
      return;
    }
  } catch (error) {
    console.error("Booking action failed:", error);

    showNotice(
      error?.response?.data?.message ||
        error?.message ||
        "Unable to complete booking action.",
      "error"
    );

    setBookingMenu(null);
  }
};
  const bookingContext = (e, stay) => {
    setBookingMenu({
      x: Math.min(e.clientX, window.innerWidth - 230),
      y: Math.min(e.clientY, window.innerHeight - 390),
      stay,
    });
  };

  const roomContext = (e, room) => {
    setRoomMenu({
      x: Math.min(e.clientX, window.innerWidth - 230),
      y: Math.min(e.clientY, window.innerHeight - 270),
      room,
    });
  };

  // Create a new stay from an empty timeline cell
  const createAt = (room = null, date = startDate) => {
    setDrawer({
      open: true,
      mode: "create",
      stay: {
        tenantName: "",
        roomId: room?.id || "",
        checkIn: date,
        checkOut: nextDay(date),
        status: "CONFIRMED",
        guests: 1,
        phone: "",
        notes: "",
      },
    });
  };

  return (
    <>
      <HeaderMenu />

      <main className="occ-page">
        <OccupancyHeader />

        <OccupancySummary summary={summary} />

        <OccupancyToolbar
          view={view}
          setView={setView}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          search={search}
          setSearch={setSearch}
          onCreateStay={() => {
            setSelectedStayId(null);
            setNewStayOpen(true);
          }}
          hostelList={hostelList}
          selectedHostel={selectedHostel}
          setSelectedHostel={setSelectedHostel}
        />

        {/* Standardized notifications */}
        {notice.message && (
          <div
            className={`occ-notice occ-notice--${notice.type}`}
            role={notice.type === "error" ? "alert" : "status"}
            aria-live={
              notice.type === "error" ? "assertive" : "polite"
            }
          >
            <span>{notice.message}</span>

            <button
              type="button"
              onClick={clearNotice}
              aria-label="Dismiss notification"
              className="occ-notice__close"
            >
              ×
            </button>
          </div>
        )}
		

        {error && <div className="occ-error">{error}</div>}

        {loading ? (
          <div className="occ-loading">Loading occupancy…</div>
        ) : (
          <OccupancyTimeline
            rooms={filteredRooms}
            stays={stays}
            startDate={startDate}
            view={view}
            onBookingClick={openSingleBill}
            onBookingDoubleClick={openSingleBill}
            onBookingContext={bookingContext}
            onRoomContext={roomContext}
            onRoomClick={openRoomDetails}
            onDropStay={dropStay}
            onResizeStay={resizeStay}
            onEmptyContext={(e, r, d) => createAt(r, d)}
          />
        )}

        <div className="occ-legend">
          <span>
            <i className="legend-confirmed" /> Confirmed
          </span>
          <span>
            <i className="legend-checked" /> Checked in
          </span>
          <span>
            <i className="legend-pending" /> Pending
          </span>
          <span>
            <i className="legend-maintenance" /> Maintenance
          </span>
        </div>

        <BookingContextMenu
          menu={bookingMenu}
          onClose={() => setBookingMenu(null)}
          onAction={bookingAction}
        />

        <RoomContextMenu
          menu={roomMenu}
          onClose={() => setRoomMenu(null)}
          onAction={handleRoomAction}
        />

        <RoomActionDialog
          open={roomDialog.open}
          action={roomDialog.action}
          room={roomDialog.room}
          roomDetailsList={
            Array.isArray(roomDetails) ? roomDetails : []
          }
          selectedRoomIndex={0}
          onClose={closeRoomDialog}
          onSave={saveRoomAction}
          loading={roomDetailsLoading}
          detailsError={roomDetailsError}
        />

        <StayDetailsDrawer
          open={drawer.open}
          stay={drawer.stay}
          rooms={rooms}
          mode={drawer.mode}
          onClose={closeDrawer}
          onSave={saveStay}
          onCheckIn={(s) => bookingAction("checkin", s)}
          onCheckOut={(s) => bookingAction("checkout", s)}
        />

       <NewStayDialog
  open={newStayOpen}
  onClose={closeSingleBill}
  stayId={selectedStayId}
  hostelId={selectedHostel}
  occupancyEditType={occupancyEditType}
/>
		{/* Custom Confirmation Dialog */}
<Dialog
  open={confirmation.open}
  onClose={closeConfirmation}
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>
    {confirmation.title}
  </DialogTitle>

  <DialogContent>
    <p style={{ margin: "8px 0" }}>
      {confirmation.message}
    </p>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button
      variant="outlined"
      onClick={closeConfirmation}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      color={confirmation.color}
      onClick={handleConfirmAction}
    >
      {confirmation.confirmText}
    </Button>
  </DialogActions>
</Dialog>
      </main>
    </>
  );
}
